'use client'

import {
    MdSchedule,
    MdPlayArrow,
    MdStop,
    MdMic,
    MdAutoAwesome,
    MdCheckCircle,
    MdCancel,
    MdAccessTime,
    MdSend,
} from 'react-icons/md'
import type { ConsultationStatus, TranscriptionStatus } from '@/lib/api/consultations/consultation-api'

interface ConsultationTimelineProps {
    status: ConsultationStatus
    transcriptionStatus: TranscriptionStatus
    scheduledAt?: string
    startedAt?: string
    completedAt?: string
    transcribedAt?: string
    aiProcessedAt?: string
    className?: string
    variant?: 'horizontal' | 'vertical'
}

interface TimelineStep {
    id: string
    label: string
    icon: React.ReactNode
    timestamp?: string
    status: 'completed' | 'current' | 'pending' | 'error'
}

export function ConsultationTimeline({
    status,
    transcriptionStatus,
    scheduledAt,
    startedAt,
    completedAt,
    transcribedAt,
    aiProcessedAt,
    className = '',
    variant = 'horizontal',
}: ConsultationTimelineProps) {
    const formatDate = (dateStr?: string): string => {
        if (!dateStr) return ''
        return new Date(dateStr).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    const getSteps = (): TimelineStep[] => {
        const steps: TimelineStep[] = []

        // 0. Solicitada (origem app — aguardando a equipe definir a data)
        steps.push({
            id: 'requested',
            label: 'Solicitada',
            icon: <MdSend />,
            status: status === 'REQUESTED' ? 'current' : 'completed',
        })

        // 1. Scheduled
        steps.push({
            id: 'scheduled',
            label: 'Agendada',
            icon: <MdSchedule />,
            timestamp: formatDate(scheduledAt),
            status: scheduledAt
                ? 'completed'
                : status === 'SCHEDULED'
                  ? 'current'
                  : status === 'REQUESTED'
                    ? 'pending'
                    : status === 'CANCELLED' || status === 'NO_SHOW'
                      ? 'error'
                      : 'pending',
        })

        // 2. Started
        const startedStatus = (): TimelineStep['status'] => {
            if (status === 'CANCELLED' || status === 'NO_SHOW') return 'error'
            if (startedAt) return 'completed'
            if (status === 'IN_PROGRESS') return 'current'
            if (status === 'WAITING') return 'current'
            return 'pending'
        }
        steps.push({
            id: 'started',
            label: status === 'WAITING' ? 'Aguardando' : 'Iniciada',
            icon: status === 'WAITING' ? <MdAccessTime /> : <MdPlayArrow />,
            timestamp: formatDate(startedAt),
            status: startedStatus(),
        })

        // 3. Recording / Completed consultation
        const recordingStatus = (): TimelineStep['status'] => {
            if (status === 'CANCELLED' || status === 'NO_SHOW') return 'error'
            if (completedAt) return 'completed'
            if (status === 'IN_PROGRESS') return 'current'
            return 'pending'
        }
        steps.push({
            id: 'recording',
            label: 'Gravação',
            icon: <MdMic />,
            timestamp: formatDate(completedAt),
            status: recordingStatus(),
        })

        // 4. Transcription
        const transcriptionStepStatus = (): TimelineStep['status'] => {
            if (status === 'CANCELLED' || status === 'NO_SHOW') return 'error'
            if (transcriptionStatus === 'COMPLETED') return 'completed'
            if (transcriptionStatus === 'PROCESSING') return 'current'
            if (transcriptionStatus === 'FAILED') return 'error'
            return 'pending'
        }
        steps.push({
            id: 'transcription',
            label: 'Transcrição',
            icon: <MdStop />,
            timestamp: formatDate(transcribedAt),
            status: transcriptionStepStatus(),
        })

        // 5. AI Analysis
        const aiStatus = (): TimelineStep['status'] => {
            if (status === 'CANCELLED' || status === 'NO_SHOW') return 'error'
            if (aiProcessedAt) return 'completed'
            if (transcriptionStatus === 'COMPLETED' && !aiProcessedAt) return 'current'
            return 'pending'
        }
        steps.push({
            id: 'ai',
            label: 'Análise IA',
            icon: <MdAutoAwesome />,
            timestamp: formatDate(aiProcessedAt),
            status: aiStatus(),
        })

        // 6. Final status
        const finalStatus = (): TimelineStep['status'] => {
            if (status === 'CANCELLED') return 'error'
            if (status === 'NO_SHOW') return 'error'
            if (status === 'COMPLETED' && aiProcessedAt) return 'completed'
            return 'pending'
        }
        steps.push({
            id: 'completed',
            label: status === 'CANCELLED' ? 'Cancelada' : status === 'NO_SHOW' ? 'Não Compareceu' : 'Concluída',
            icon: status === 'CANCELLED' || status === 'NO_SHOW' ? <MdCancel /> : <MdCheckCircle />,
            status: finalStatus(),
        })

        return steps
    }

    const steps = getSteps()

    const getStepColor = (stepStatus: TimelineStep['status']): string => {
        switch (stepStatus) {
            case 'completed':
                return 'text-success bg-success/10 border-success'
            case 'current':
                return 'text-secondary bg-secondary/10 border-secondary animate-pulse'
            case 'error':
                return 'text-error bg-error/10 border-error'
            default:
                return 'text-base-content/30 bg-base-200 border-base-300'
        }
    }

    const getLineColor = (stepStatus: TimelineStep['status']): string => {
        switch (stepStatus) {
            case 'completed':
                return 'bg-success'
            case 'error':
                return 'bg-error'
            default:
                return 'bg-base-300'
        }
    }

    if (variant === 'vertical') {
        return (
            <div className={`flex flex-col ${className}`}>
                {steps.map((step, index) => (
                    <div key={step.id} className="flex gap-3">
                        <div className="flex flex-col items-center">
                            <div
                                className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-lg ${getStepColor(
                                    step.status
                                )}`}
                            >
                                {step.icon}
                            </div>
                            {index < steps.length - 1 && (
                                <div
                                    className={`w-0.5 h-12 ${getLineColor(step.status)}`}
                                />
                            )}
                        </div>
                        <div className="pb-8">
                            <p className="font-medium text-sm text-base-content">
                                {step.label}
                            </p>
                            {step.timestamp && (
                                <p className="text-xs text-base-content/60">
                                    {step.timestamp}
                                </p>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    return (
        <div className={`flex items-center justify-between ${className}`}>
            {steps.map((step, index) => (
                <div key={step.id} className="flex items-center flex-1">
                    <div className="flex flex-col items-center">
                        <div
                            className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm ${getStepColor(
                                step.status
                            )}`}
                            title={step.timestamp || step.label}
                        >
                            {step.icon}
                        </div>
                        <p className="text-xs text-base-content/60 mt-1 text-center max-w-[80px] truncate">
                            {step.label}
                        </p>
                    </div>
                    {index < steps.length - 1 && (
                        <div
                            className={`h-0.5 flex-1 mx-2 ${getLineColor(step.status)}`}
                        />
                    )}
                </div>
            ))}
        </div>
    )
}
